import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShoppingCart, Search, Plus, Minus, Trash2, X, User, Home, Pause, ClipboardList,
  RefreshCcw, CreditCard, Zap, ArrowLeft, Printer,
} from "lucide-react";
import { api } from "../../../lib/auth";
import { useConfirm } from "../../../components/ConfirmDialog";
import { fmtMoney as fmt, getCurrencySymbol } from "../../../lib/currency";

export default function POS() {
  const { subdomain } = useParams();
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);

  const [warehouseId, setWarehouseId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [brandFilter, setBrandFilter] = useState("all");
  const [search, setSearch] = useState("");

  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`pos_cart_${subdomain}`) || "[]"); } catch { return []; }
  });
  const [orderTax, setOrderTax] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [shipping, setShipping] = useState(0);
  const [paying, setPaying] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const confirm = useConfirm();

  useEffect(() => {
    api.get("/t/products").then((r) => setProducts(r.data));
    api.get("/t/customers").then((r) => {
      setCustomers(r.data);
      const walkin = r.data.find((c) => c.name.toLowerCase().includes("walk")) || r.data[0];
      if (walkin) setCustomerId(walkin.id);
    });
    api.get("/t/warehouses").then((r) => {
      setWarehouses(r.data);
      if (r.data[0]) setWarehouseId(r.data[0].id);
    });
    api.get("/t/categories").then((r) => setCats(r.data));
    api.get("/t/brands").then((r) => setBrands(r.data));
  }, []);

  useEffect(() => { localStorage.setItem(`pos_cart_${subdomain}`, JSON.stringify(cart)); }, [cart, subdomain]);

  const filtered = useMemo(() => {
    let list = products;
    if (catFilter !== "all") list = list.filter((p) => p.category_id === catFilter);
    if (brandFilter !== "all") list = list.filter((p) => p.brand_id === brandFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q) || p.code?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q));
    }
    return list;
  }, [products, catFilter, brandFilter, search]);

  const addToCart = (p) => {
    setCart((c) => {
      const ex = c.find((i) => i.product_id === p.id);
      if (ex) return c.map((i) => i.product_id === p.id ? { ...i, qty: i.qty + 1 } : i);
      return [...c, { product_id: p.id, name: p.name, unit_price: p.price, qty: 1, discount: 0, tax: 0, stock: p.stock }];
    });
  };
  const updateQty = (pid, delta) => setCart((c) => c.map((i) => i.product_id === pid ? { ...i, qty: Math.max(1, i.qty + delta) } : i));
  const setQtyDirect = (pid, q) => setCart((c) => c.map((i) => i.product_id === pid ? { ...i, qty: Math.max(1, Number(q) || 1) } : i));
  const remove = (pid) => setCart((c) => c.filter((i) => i.product_id !== pid));

  const subtotal = cart.reduce((s, i) => s + i.qty * i.unit_price, 0);
  const tax = (subtotal * orderTax) / 100;
  const total = subtotal - discount + shipping + tax;
  const count = cart.reduce((s, i) => s + i.qty, 0);

  const pay = async () => {
    if (cart.length === 0) return toast.error("Cart is empty");
    if (!warehouseId) return toast.error("Pick a warehouse");
    if (!(await confirm({ title: "Confirm Sale?", message: `Charge ${fmt(total)} for ${count} item(s)?`, yesLabel: "Confirm Sale" }))) return;
    setPaying(true);
    try {
      const customer = customers.find((c) => c.id === customerId);
      const items = cart.map((i) => ({ product_id: i.product_id, name: i.name, qty: i.qty, unit_price: i.unit_price, discount: i.discount, tax: i.tax }));
      const r = await api.post("/t/sales", {
        date: new Date().toISOString().slice(0, 10),
        customer_id: customerId, customer_name: customer?.name || "Walk-in Customer",
        warehouse_id: warehouseId,
        items,
        order_tax: orderTax, order_discount: discount, shipping,
        paid: total, payment_method: "cash", status: "completed",
        channel: "pos",
      });
      toast.success(`Sale completed — ${fmt(total)}`);
      const sale = r.data;
      setLastSale({
        reference: sale?.reference || `POS-${Date.now()}`,
        customer_name: customer?.name || "Walk-in Customer",
        items, subtotal, tax, discount, shipping, total,
        paid: total, change: 0, date: new Date(),
      });
      setCart([]); setOrderTax(0); setDiscount(0); setShipping(0);
      localStorage.removeItem(`pos_cart_${subdomain}`);
      api.get("/t/products").then((r) => setProducts(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setPaying(false); }
  };

  const printReceipt = () => {
    const w = window.open("", "_blank", "width=320,height=600");
    if (!w) return toast.error("Popup blocked — allow popups to print receipts");
    const tenant = JSON.parse(localStorage.getItem("brivo_tenant") || "{}");
    const r = lastSale;
    const html = `<html><head><title>Receipt ${r.reference}</title>
      <style>
        @page { margin: 0; size: 80mm auto; }
        body { font-family: -apple-system, "Segoe UI", monospace; font-size: 11px; padding: 8px; color: #000; }
        h1 { font-size: 14px; text-align: center; margin: 4px 0; }
        .muted { color: #666; font-size: 10px; }
        .row { display: flex; justify-content: space-between; margin: 2px 0; }
        hr { border: none; border-top: 1px dashed #999; margin: 6px 0; }
        table { width: 100%; border-collapse: collapse; }
        th, td { text-align: left; padding: 2px 0; font-size: 10.5px; }
        td.r, th.r { text-align: right; }
        .total { font-size: 14px; font-weight: 800; }
        .center { text-align: center; }
      </style></head><body>
      <h1>${tenant.company_name || "BRIVO"}</h1>
      <div class="center muted">${tenant.subdomain || ""}.brivo360.com</div>
      <hr/>
      <div class="row"><span>Receipt</span><strong>${r.reference}</strong></div>
      <div class="row"><span>Date</span><span>${r.date.toLocaleString()}</span></div>
      <div class="row"><span>Customer</span><span>${r.customer_name}</span></div>
      <hr/>
      <table>
        <thead><tr><th>Item</th><th class="r">Qty</th><th class="r">Price</th><th class="r">Total</th></tr></thead>
        <tbody>
          ${r.items.map((it) => `<tr><td>${it.name}</td><td class="r">${it.qty}</td><td class="r">${Number(it.unit_price).toFixed(2)}</td><td class="r">${(it.qty * it.unit_price).toFixed(2)}</td></tr>`).join("")}
        </tbody>
      </table>
      <hr/>
      <div class="row"><span>Subtotal</span><span>${r.subtotal.toFixed(2)}</span></div>
      ${r.discount > 0 ? `<div class="row"><span>Discount</span><span>-${r.discount.toFixed(2)}</span></div>` : ""}
      ${r.tax > 0 ? `<div class="row"><span>Tax</span><span>${r.tax.toFixed(2)}</span></div>` : ""}
      ${r.shipping > 0 ? `<div class="row"><span>Shipping</span><span>${r.shipping.toFixed(2)}</span></div>` : ""}
      <div class="row total"><span>TOTAL</span><span>${getCurrencySymbol()}${r.total.toFixed(2)}</span></div>
      <div class="row"><span>Paid</span><span>${getCurrencySymbol()}${r.paid.toFixed(2)}</span></div>
      <hr/>
      <div class="center muted">Thank you for your purchase!</div>
      <div class="center muted">Powered by Brivo POS</div>
      <script>window.onload = () => { window.print(); setTimeout(() => window.close(), 400); };</script>
      </body></html>`;
    w.document.write(html);
    w.document.close();
  };

  return (
    <div data-testid="pos-page" className="h-screen flex flex-col bg-[#F4F5F0] -m-6 lg:-m-0">
      {/* POS toolbar */}
      <div className="bg-white border-b border-black/8 px-4 py-2.5 flex items-center gap-2 flex-wrap">
        <button onClick={() => nav(`/t/${subdomain}/dashboard`)} className="p-2 rounded-lg hover:bg-black/5"><ArrowLeft size={16} /></button>
        <div className="w-9 h-9 rounded-lg bg-[#1FA868] text-white flex items-center justify-center"><Zap size={16} /></div>
        <span className="text-[11px] font-bold uppercase text-[#1FA868]">Open Register</span>
        <div className="flex-1" />
        <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} data-testid="pos-warehouse"
          className="px-3 py-2 rounded-lg border border-black/15 bg-white text-sm font-semibold focus:border-[#1FA868] outline-none">
          <option value="">Select Warehouse</option>
          {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} data-testid="pos-category"
          className="px-3 py-2 rounded-lg border border-black/15 bg-white text-sm focus:border-[#1FA868] outline-none">
          <option value="all">All Categories</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} data-testid="pos-brand"
          className="px-3 py-2 rounded-lg border border-black/15 bg-white text-sm focus:border-[#1FA868] outline-none">
          <option value="all">All Brands</option>
          {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} data-testid="pos-customer"
          className="px-3 py-2 rounded-lg border border-black/15 bg-white text-sm focus:border-[#1FA868] outline-none">
          {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* LEFT: Cart */}
        <aside className="w-full lg:w-[420px] bg-white border-b lg:border-b-0 lg:border-r border-black/8 flex flex-col max-h-[50vh] lg:max-h-none">
          <div className="p-4 border-b border-black/8 flex items-center justify-between">
            <h2 className="font-display font-bold text-lg">Checkout</h2>
            <button onClick={() => { setCart([]); localStorage.removeItem(`pos_cart_${subdomain}`); }}
              data-testid="pos-reset"
              className="text-xs text-rose-600 font-semibold inline-flex items-center gap-1 hover:underline">
              <RefreshCcw size={11} /> Reset
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {cart.length === 0 ? (
              <div className="p-10 text-center text-black/45">
                <ShoppingCart size={42} className="mx-auto mb-3 opacity-40" />
                <div className="font-semibold text-sm">No items added</div>
                <div className="text-xs mt-1">Tap products from the right panel</div>
              </div>
            ) : (
              <div className="divide-y divide-black/5">
                {cart.map((i) => (
                  <div key={i.product_id} className="p-3 hover:bg-[#FAFBFB]" data-testid={`cart-item-${i.product_id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#E8F5E9] text-[#1FA868] font-bold flex items-center justify-center text-[10px] flex-shrink-0">
                        {(i.name || "?").slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-black truncate">{i.name}</div>
                        <div className="text-xs text-black/55 tabular-nums">{fmt(i.unit_price)}</div>
                      </div>
                      <button onClick={() => remove(i.product_id)} className="p-1 rounded text-rose-500 hover:bg-rose-50"><Trash2 size={12} /></button>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="inline-flex items-center gap-1 bg-[#F4F5F0] rounded-lg px-1 py-0.5">
                        <button onClick={() => updateQty(i.product_id, -1)} className="p-1 rounded hover:bg-white"><Minus size={11} /></button>
                        <input value={i.qty} onChange={(e) => setQtyDirect(i.product_id, e.target.value)}
                          className="w-10 text-center bg-transparent text-sm font-bold focus:outline-none" />
                        <button onClick={() => updateQty(i.product_id, 1)} className="p-1 rounded hover:bg-white"><Plus size={11} /></button>
                      </div>
                      <div className="font-bold text-sm tabular-nums">{fmt(i.qty * i.unit_price)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Totals & controls */}
          <div className="border-t border-black/8 p-4 space-y-2 bg-[#FAFBFB]">
            <div className="grid grid-cols-3 gap-2 text-xs">
              <label className="block">
                <span className="block text-[9px] uppercase font-bold text-black/55 mb-0.5">Tax %</span>
                <input type="number" value={orderTax} onChange={(e) => setOrderTax(Number(e.target.value) || 0)}
                  data-testid="pos-tax" className="w-full px-2 py-1.5 rounded border border-black/15 text-xs tabular-nums" />
              </label>
              <label className="block">
                <span className="block text-[9px] uppercase font-bold text-black/55 mb-0.5">Discount</span>
                <input type="number" value={discount} onChange={(e) => setDiscount(Number(e.target.value) || 0)}
                  data-testid="pos-discount" className="w-full px-2 py-1.5 rounded border border-black/15 text-xs tabular-nums" />
              </label>
              <label className="block">
                <span className="block text-[9px] uppercase font-bold text-black/55 mb-0.5">Ship</span>
                <input type="number" value={shipping} onChange={(e) => setShipping(Number(e.target.value) || 0)}
                  data-testid="pos-shipping" className="w-full px-2 py-1.5 rounded border border-black/15 text-xs tabular-nums" />
              </label>
            </div>
            <div className="text-sm space-y-0.5">
              <div className="flex justify-between text-black/65"><span>Subtotal</span><span className="tabular-nums">{fmt(subtotal)}</span></div>
              <div className="flex justify-between text-black/65"><span>Tax</span><span className="tabular-nums">{fmt(tax)}</span></div>
              <div className="flex justify-between text-rose-600"><span>Discount</span><span className="tabular-nums">-{fmt(discount)}</span></div>
            </div>
            <div className="pt-2 border-t border-black/10 flex items-end justify-between">
              <div className="text-[10px] font-bold uppercase text-black/55">Grand Total</div>
              <div className="font-display font-extrabold text-2xl text-[#1FA868] tabular-nums">{fmt(total)}</div>
            </div>
            <button onClick={pay} disabled={paying || cart.length === 0} data-testid="pos-pay"
              className="w-full py-3 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white font-bold text-sm inline-flex items-center justify-center gap-2 hover:opacity-95 disabled:opacity-50">
              <CreditCard size={14} /> {paying ? "Processing…" : `Pay Now · ${count} item${count !== 1 ? "s" : ""}`}
            </button>
          </div>
        </aside>

        {/* RIGHT: Product grid */}
        <div className="flex-1 flex flex-col min-h-0">
          <div className="p-3 border-b border-black/8 bg-white">
            <div className="flex items-center gap-2 bg-[#F4F5F0] rounded-lg px-3 py-2.5">
              <Search size={16} className="text-black/40" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} autoFocus
                placeholder="Scan/Search Product by Code or Name" data-testid="pos-search"
                className="flex-1 bg-transparent focus:outline-none text-sm" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {filtered.length === 0 ? (
              <div className="py-20 text-center text-black/40">
                <Search size={42} className="mx-auto mb-3 opacity-40" />
                <div>No products found</div>
              </div>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                {filtered.map((p) => (
                  <button key={p.id} onClick={() => addToCart(p)} data-testid={`pos-product-${p.id}`}
                    className="bg-white rounded-xl border border-black/8 p-3 text-left hover:border-[#1FA868] hover:shadow-md transition-all group">
                    <div className="aspect-square rounded-lg bg-[#F4F5F0] overflow-hidden mb-2 flex items-center justify-center font-display font-bold text-2xl text-black/20 relative">
                      {p.image_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${p.image_url}`} alt="" className="w-full h-full object-cover" /> : (p.name || "?").slice(0, 2).toUpperCase()}
                      {p.stock <= p.stock_alert && <span className="absolute top-1 right-1 text-[8px] font-bold bg-rose-500 text-white px-1.5 py-0.5 rounded">LOW</span>}
                    </div>
                    <div className="font-semibold text-xs text-black line-clamp-2 min-h-[2.5em]">{p.name}</div>
                    <div className="mt-1 flex items-end justify-between">
                      <div className="font-display font-extrabold text-sm text-[#1FA868] tabular-nums">{fmt(p.price)}</div>
                      <div className="text-[9px] text-black/45 font-bold">{p.stock}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Receipt modal — shown after a successful sale */}
      {lastSale && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="receipt-modal">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl">
            <div className="p-5 text-center">
              <div className="w-14 h-14 bg-[#E8F5E9] text-[#1FA868] rounded-full flex items-center justify-center mx-auto mb-3">
                <CreditCard size={26} />
              </div>
              <h3 className="font-extrabold text-lg">Sale Completed</h3>
              <p className="text-xs text-black/55">Reference: <span className="font-mono">{lastSale.reference}</span></p>
              <div className="text-3xl font-extrabold text-[#1FA868] mt-3">{fmt(lastSale.total)}</div>
              <div className="text-xs text-black/55 mt-1">{lastSale.items.length} item(s) · {lastSale.customer_name}</div>
            </div>
            <div className="border-t border-black/5 p-4 flex gap-2">
              <button onClick={() => setLastSale(null)} data-testid="receipt-close" className="flex-1 px-3 py-2.5 rounded-xl bg-white border border-black/10 text-sm font-bold">Close</button>
              <button onClick={printReceipt} data-testid="receipt-print" className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white text-sm font-bold">
                <Printer size={14} /> Print Receipt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
